/**
 * 设备列表拉取辅助（从 Dashboard 拆出）
 */
import { resolveDisplayDaysFromTrackMode } from './trackQueryHelpers';
import {
  readResponseJsonSafe,
  isBackendUnavailableError,
  getDeviceFetchMaxRetries
} from './httpResponseHelpers';

export function shouldThrottleDeviceFetch(lastRequestTime, minIntervalMs, now = Date.now()) {
  return now - lastRequestTime < minIntervalMs;
}

export function getDeviceFetchRetryDelayMs(retryCount) {
  return Math.pow(2, retryCount) * 5000;
}

export { isBackendUnavailableError, getDeviceFetchMaxRetries };

/**
 * 解析 display_days；无法从轨迹模式得出时走地图设置回调
 */
export async function resolveDeviceListDisplayDays(options = {}) {
  const {
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    startTime = '',
    endTime = '',
    fetchMapDisplayDays
  } = options;

  const resolved = resolveDisplayDaysFromTrackMode(trackMode, {
    trackMinutes,
    trackHours,
    trackDays,
    startTime,
    endTime
  });

  if (resolved.displayDays != null) {
    return { displayDays: resolved.displayDays, reason: resolved.reason, source: 'trackMode' };
  }

  if (typeof fetchMapDisplayDays === 'function') {
    try {
      const days = await fetchMapDisplayDays();
      return {
        displayDays: days != null ? days : 365,
        reason: `地图显示设置: ${days != null ? days : 365}天`,
        source: 'mapSettings'
      };
    } catch (_) {
      /* fall through */
    }
  }

  return { displayDays: 365, reason: '默认值365天', source: 'default' };
}

export function buildDeviceListUrl(apiBase, displayDays, appDisplayDays = 0) {
  const base = String(apiBase || '').replace(/\/$/, '');
  const days = Number(displayDays) || 365;
  const appDays = Math.min(30, Math.max(0, Number(appDisplayDays) || 0));
  let url = `${base}/device/list?display_days=${days}`;
  if (appDays > 0) url += `&app_display_days=${appDays}`;
  return url;
}

/**
 * 执行设备列表拉取（纯流程，由 Dashboard 负责写 state）
 * @returns {Promise<{ action: string, ... }>}
 */
export async function runDashboardDeviceFetch(options = {}) {
  const {
    retryCount = 0,
    isRequestInProgress = false,
    lastRequestTime = 0,
    minIntervalMs = 1000,
    now = Date.now(),
    skipGuards = false,
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    getStartTime,
    getEndTime,
    apiBase,
    requestManager,
    apiFetch,
    classifyQuality,
    authToken,
    appDisplayDays = 0
  } = options;

  if (!skipGuards) {
    if (isRequestInProgress) {
      return { action: 'skip', reason: 'in-progress' };
    }
    if (shouldThrottleDeviceFetch(lastRequestTime, minIntervalMs, now)) {
      return { action: 'skip', reason: 'throttle' };
    }
  }

  const fetchStartTime = Date.now();
  try {
    const safeStartTime = (typeof getStartTime === 'function' ? getStartTime() : '') || '';
    const safeEndTime = (typeof getEndTime === 'function' ? getEndTime() : '') || '';
    const displayDaysResolved = await resolveDeviceListDisplayDays({
      trackMode,
      trackMinutes,
      trackHours,
      trackDays,
      startTime: safeStartTime,
      endTime: safeEndTime,
      fetchMapDisplayDays: async () => {
        const settingsRes = await apiFetch('/admin/map-settings');
        const settingsData = await settingsRes.json();
        return settingsData.success ? settingsData.settings.map_display_days : 365;
      }
    });
    const displayDays = displayDaysResolved.displayDays;
    const fullUrl = buildDeviceListUrl(apiBase, displayDays, appDisplayDays);
    const res = await requestManager.makeRequest(fullUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken || ''}`,
        'Content-Type': 'application/json'
      }
    });
    const responseTime = Date.now() - fetchStartTime;
    const parsed = await readResponseJsonSafe(res);
    if (!res || res.ok === false) {
      const status = res?.status || 'unknown';
      const detail = (parsed.text || '').slice(0, 160);
      const err = new Error(
        detail
          ? `设备列表请求失败 (HTTP ${status}): ${detail}`
          : `设备列表请求失败 (HTTP ${status})`
      );
      err.status = status;
      err.responseText = detail;
      throw err;
    }
    if (!parsed.ok) {
      const err = new Error(`设备列表返回非 JSON（可能是代理/后端不可用）：${parsed.error?.message || 'parse-failed'}`);
      err.responseText = (parsed.text || '').slice(0, 160);
      throw err;
    }

    const data = parsed.data || {};
    if (!data.success) {
      throw new Error('API返回失败: ' + (data.message || '未知错误'));
    }

    const devices = Array.isArray(data.devices) ? data.devices : [];
    const memberVisibility = data.member_visibility || data.meta?.member_visibility || '';
    const quality = typeof classifyQuality === 'function'
      ? classifyQuality(responseTime)
      : 'good';

    return {
      action: 'success',
      devices,
      memberVisibility,
      responseTime,
      quality,
      displayDaysResolved,
      trackModeSnapshot: {
        trackMode,
        trackMinutes,
        trackHours,
        trackDays,
        startTime: safeStartTime,
        endTime: safeEndTime
      }
    };
  } catch (error) {
    const responseTime = Date.now() - fetchStartTime;
    const responseText = error?.responseText || '';
    const maxRetries = getDeviceFetchMaxRetries(error, responseText);
    const unavailable = isBackendUnavailableError(error, responseText);

    if (retryCount < maxRetries) {
      return {
        action: 'retry',
        error,
        responseTime,
        retryCount,
        retryDelay: unavailable ? 2000 : getDeviceFetchRetryDelayMs(retryCount),
        unavailable
      };
    }

    // 后端/代理明显不可用时，跳过备用接口（同样会失败，只增加噪音）
    if (unavailable) {
      return {
        action: 'error',
        error,
        responseTime,
        unavailable: true
      };
    }

    try {
      const res = await apiFetch('device/list?display_days=365');
      const parsed = await readResponseJsonSafe(res);
      if (res.ok && parsed.ok && parsed.data?.success) {
        return {
          action: 'fallback-success',
          devices: parsed.data.devices,
          responseTime,
          error
        };
      }
      return {
        action: 'error',
        error,
        fallbackError: parsed.error || new Error(`fallback HTTP ${res?.status}`),
        responseTime
      };
    } catch (fallbackError) {
      return {
        action: 'error',
        error,
        fallbackError,
        responseTime
      };
    }
  }
}
